/**
 * The two leaderboards.
 *
 * Main: average points per game, then win %, then point difference. Everyone is ranked; a player
 * under the podium minimum is marked ineligible, not hidden. Equal on all three means an equal
 * rank (1, 2, 2, 4), with the name only deciding the order they are listed in.
 * Fun: the bench (bounties won and right picks, from bench.ts) and, once they are handed out,
 * the awards (awards.ts). Mini games carry no fun points (decision 33).
 */

import { awardPoints } from "./awards";
import { benchPoints } from "./bench";
import { gameResult } from "./scoring";
import type { PlayerId, TournamentState } from "./types";

export interface MainRow {
  id: PlayerId;
  name: string;
  games: number;
  wins: number;
  points: number;
  against: number;
  avg: number;
  winPct: number;
  diff: number;
  eligible: boolean;
  rank: number;
}

export interface FunRow {
  id: PlayerId;
  name: string;
  funPoints: number;
  eventGames: number;
  eventWins: number;
  rank: number;
  /** Has anything to show: a bounty won, a pick settled, an award. */
  active: boolean;
}

const EPS = 1e-9;

function near(x: number, y: number): boolean {
  return Math.abs(x - y) < EPS;
}

export function mainBoard(state: TournamentState): MainRow[] {
  const acc = new Map<PlayerId, { games: number; wins: number; points: number; against: number }>();
  for (const id of Object.keys(state.players)) acc.set(id, { games: 0, wins: 0, points: 0, against: 0 });
  for (const round of state.rounds) {
    for (const game of round.games) {
      const result = gameResult(game, state.config);
      if (!result) continue;
      for (const side of [result.a, result.b]) {
        for (const id of side.players) {
          const row = acc.get(id);
          if (!row) continue; // a removed player: their games stay in the log, off the board
          row.games += 1;
          row.wins += side.won ? 1 : 0;
          row.points += side.mainPoints;
          row.against += side.mainAgainst;
        }
      }
    }
  }
  const rows: MainRow[] = [];
  for (const [id, r] of acc) {
    const p = state.players[id];
    if (!p) continue;
    rows.push({
      id,
      name: p.name,
      games: r.games,
      wins: r.wins,
      points: r.points,
      against: r.against,
      avg: r.games ? r.points / r.games : 0,
      winPct: r.games ? r.wins / r.games : 0,
      diff: r.points - r.against,
      eligible: r.games >= state.config.minGamesForPodium,
      rank: 0,
    });
  }
  rows.sort((x, y) => {
    if (!near(x.avg, y.avg)) return y.avg - x.avg;
    if (!near(x.winPct, y.winPct)) return y.winPct - x.winPct;
    if (!near(x.diff, y.diff)) return y.diff - x.diff;
    return x.name.localeCompare(y.name, "en");
  });
  rows.forEach((row, i) => {
    const prev = rows[i - 1];
    row.rank = prev && near(prev.avg, row.avg) && near(prev.winPct, row.winPct) && near(prev.diff, row.diff)
      ? prev.rank
      : i + 1;
  });
  return rows;
}

/** The eligible players only, re-ranked among themselves: the reveal's top three come from here. */
export function podium(board: MainRow[]): MainRow[] {
  const eligible = board.filter((r) => r.eligible).map((r) => ({ ...r }));
  eligible.forEach((row, i) => {
    const prev = eligible[i - 1];
    row.rank = prev && near(prev.avg, row.avg) && near(prev.winPct, row.winPct) && near(prev.diff, row.diff)
      ? prev.rank
      : i + 1;
  });
  return eligible;
}

export function funBoard(state: TournamentState): FunRow[] {
  const acc = new Map<PlayerId, { funPoints: number; eventGames: number; eventWins: number }>();
  const bench = benchPoints(state);
  const prizes = state.reveal.awards ? awardPoints(state) : new Map<PlayerId, number>();
  for (const id of Object.keys(state.players)) {
    const b = bench.get(id);
    const funPoints = state.config.bench.startingFun + (b ? b.bounty + b.picks : 0) + (prizes.get(id) ?? 0);
    acc.set(id, { funPoints, eventGames: 0, eventWins: 0 });
  }
  for (const round of state.rounds) {
    for (const game of round.games) {
      const result = gameResult(game, state.config);
      if (!result || !result.event) continue;
      for (const side of [result.a, result.b]) {
        for (const id of side.players) {
          const row = acc.get(id);
          if (!row) continue;
          row.funPoints += side.funPoints;
          row.eventGames += 1;
          row.eventWins += side.won ? 1 : 0;
        }
      }
    }
  }
  const rows: FunRow[] = [];
  for (const [id, r] of acc) {
    const p = state.players[id];
    if (!p) continue;
    const b = bench.get(id);
    const active = r.funPoints !== state.config.bench.startingFun || Boolean(b && b.right + b.wrong > 0);
    rows.push({ id, name: p.name, ...r, active, rank: 0 });
  }
  rows.sort((x, y) => {
    if (!near(x.funPoints, y.funPoints)) return y.funPoints - x.funPoints;
    if (x.eventWins !== y.eventWins) return y.eventWins - x.eventWins;
    return x.name.localeCompare(y.name, "en");
  });
  rows.forEach((row, i) => {
    const prev = rows[i - 1];
    row.rank = prev && near(prev.funPoints, row.funPoints) && prev.eventWins === row.eventWins ? prev.rank : i + 1;
  });
  return rows;
}
